import { useState, useEffect } from "react";
import MyTable from "../../Components/MyTable";
import Mytitle from "../../Components/Mytitle";
import ModalForm from "../../Components/ModalForm";
import MyInput from "../../Components/Myinput";
import API from "../../Api/axiosConfig";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faEye } from "@fortawesome/free-solid-svg-icons";
import Can from "../../Components/Can";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../context/SettingsContext";
import { numberToWords } from "../../utils/numberToWords";
import { SquarePen } from "lucide-react";
import { toLocal } from "../../utils/toLocal";

function ReceiptVoucher() {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: "",
    amount: "",
    date: new Date().toISOString().split("T")[0],
    we: "",
    textValue: "",
    for: "",
    payment_type: "",
    invoice_number: "",
    note: "",
    type: "receive",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [nextInvoice, setNextInvoice] = useState();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);

  useEffect(() => {
    API.get("/get-all-customers")
      .then((res) => {
        setCustomers(
          res.data.data.filter((c) => c.type === "customer") || []
        );
      })
      .catch(() => toast.error(t("moneyInvoices_loadCustomersFailed")));

    API.get("/next-money-invoice-number")
      .then((res) => setNextInvoice(res.data.next_invoice_number || null))
      .catch(() => toast.error(t("moneyInvoices_loadNextInvoiceFailed")));
  }, [t]);

  useEffect(() => {
    if (formData.amount) {
      const words = numberToWords(formData.amount, i18n.language);
      setFormData((prev) => ({ ...prev, textValue: words }));
    } else {
      setFormData((prev) => ({ ...prev, textValue: "" }));
    }
  }, [formData.amount, i18n.language]);

  const handleAdd = () => {
    setFormData({
      customer_id: "",
      amount: "",
      date: new Date().toISOString().split("T")[0],
      we:
        i18n.language === "ar"
          ? `استلمنا نحن ${settings?.company_name || ""}`
          : `Received by us ${settings?.company_name || ""}`,
      textValue: "",
      for: "",
      payment_type: "",
      invoice_number: nextInvoice,
      note: "",
      type: "receive",
    });
    setEditId(null);
    setErrors({});
    setShowModal(true);
  };

  const handleEdit = (record) => {
    setFormData({ ...record, customer_id: record.customer_id || "" });
    setEditId(record.uuid);
    setErrors({});
    setShowModal(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await API.delete(`/money-invoices/${deleteId}`);
      setRefreshKey((prev) => prev + 1);
      setShowDeleteModal(false);
      setDeleteId(null);
    } catch (err) {
      toast.error(t("moneyInvoices_deleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    try {
      const payload = { ...formData, type: "receive" };
      if (editId) {
        await API.put(`/money-invoices/${editId}`, payload);
      } else {
        await API.post("/money-invoices", payload);
      }
      setShowModal(false);
      API.get("/next-money-invoice-number")
        .then((res) => setNextInvoice(res.data.next_invoice_number || null))
        .catch(() => {});
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        toast.error(t("moneyInvoices_saveError"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShowDetails = (voucher) => {
    setSelectedVoucher(voucher);
    setShowDetailsModal(true);
  };

  const columns = [
    { header: t("invoice_number"), accessor: "invoice_number" },
    { header: t("moneyInvoices_amount"), accessor: (row) => toLocal(row.amount) },
    { header: t("moneyInvoices_date"), accessor: "date" },
    { header: t("customer"), accessor: (row) => row.customer?.name || row.supplier?.name || "-" },
    {
      header: t("moneyInvoices_actions"),
      accessor: (row) => (
        <div style={{ width: "fit-content" }} className="d-flex border mx-auto rounded">
          <Can permission="money_invoice_recive_show">
            <button
              className="btn btn-sm mx-1 extrabtn"
              onClick={() => handleShowDetails(row)}
              title={t("moneyInvoices_viewDetails")}
            >
              <FontAwesomeIcon icon={faEye} />
            </button>
          </Can>
          <Can permission="money_invoice_recive_update">
            <button
              className="btn btn-sm btn-primary mx-1 no-style"
              onClick={() => handleEdit(row)}
            >
              <SquarePen />
            </button>
          </Can>
          <Can permission="money_invoice_recive_delete">
            <button
              className="btn btn-sm btn-danger no-style"
              onClick={() => handleDeleteClick(row.uuid)}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </Can>
        </div>
      ),
      hideOnPrint: true,
    },
  ];

  return (
    <div className="container mt-5">
      <Mytitle title={t("receipt_voucher_title")} />

      {/* Modal: عرض التفاصيل */}
      <ModalForm
        show={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title={t("receipt_voucher_title")}
        mode="print"
      >
        {selectedVoucher ? (
          <div className="p-1" dir={i18n.language === "ar" ? "rtl" : "ltr"}>
            <h5 className="fw-bold text-center mb-4 pb-2 border-bottom">
              {t("receipt_voucher_title")} ({selectedVoucher?.invoice_number})
            </h5>

            <div className="row g-3">
              <div className="col-6">
                <label className="fw-bold text-muted small">{t("date")}:</label>
                <p className="border-bottom pb-1">{selectedVoucher?.date || "-"}</p>
              </div>
              <div className="col-6">
                <label className="fw-bold text-muted small">{t("moneyInvoices_amount")}:</label>
                <p className="border-bottom pb-1 fw-bold text-success">{toLocal(selectedVoucher?.amount) || "-"}</p>
              </div>
              <div className="col-6">
                <label className="fw-bold text-muted small">{t("customer")}:</label>
                <p className="border-bottom pb-1">{selectedVoucher?.customer?.name || "-"}</p>
              </div>
              <div className="col-6">
                <label className="fw-bold text-muted small">{t("moneyInvoices_phone")}:</label>
                <p className="border-bottom pb-1">{selectedVoucher?.customer?.phone || "-"}</p>
              </div>
              <div className="col-6">
                <label className="fw-bold text-muted small">{t("moneyInvoices_receivedByUs")}:</label>
                <p className="border-bottom pb-1">{selectedVoucher?.we || "-"}</p>
              </div>
              <div className="col-6">
                <label className="fw-bold text-muted small">{t("payment_method")}:</label>
                <p className="border-bottom pb-1">
                  {selectedVoucher?.payment_type === "cash"
                    ? t("payment_cash")
                    : selectedVoucher?.payment_type === "bank_installment"
                    ? t("payment_bank_installment")
                    : selectedVoucher?.payment_type === "direct_installment"
                    ? t("payment_direct_installment")
                    : selectedVoucher?.payment_type === "network"
                    ? t("payment_network")
                    : "-"}
                </p>
              </div>
              {selectedVoucher?.textValue && (
                <div className="col-12">
                  <label className="fw-bold text-muted small">{t("moneyInvoices_amountWritten")}:</label>
                  <p className="border-bottom pb-1">{selectedVoucher?.textValue}</p>
                </div>
              )}
              {selectedVoucher?.for && (
                <div className="col-12">
                  <label className="fw-bold text-muted small">{t("moneyInvoices_for")}:</label>
                  <p className="border-bottom pb-1">{selectedVoucher?.for}</p>
                </div>
              )}
              {selectedVoucher?.note && (
                <div className="col-12">
                  <label className="fw-bold text-muted small">{t("notes")}:</label>
                  <p className="border-bottom pb-1">{selectedVoucher?.note}</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <p>{t("moneyInvoices_noData")}</p>
        )}
      </ModalForm>

      {/* Modal: إضافة / تعديل */}
      <ModalForm
        show={showModal}
        onClose={() => setShowModal(false)}
        title={`${editId ? t("moneyInvoices_editVoucher") : t("receipt_voucher_add")} (${
          formData.invoice_number ? formData.invoice_number : nextInvoice
        })`}
        onSubmit={handleSubmit}
        loading={loading}
        mode="form"
      >
        <div className="form-grid">
          {/* العميل */}
          <div className="form-group">
            <MyInput
              as="select"
              label={t("customer")}
              value={formData.customer_id}
              onChange={(e) =>
                setFormData({ ...formData, customer_id: e.target.value })
              }
              error={errors.customer_id?.[0]}
              options={[
                { value: "", label: t("moneyInvoices_selectCustomer") },
                ...customers.map((c) => ({ value: c.id, label: c.name })),
              ]}
              required={true}
            />
          </div>

          {/* المبلغ */}
          <div className="form-group">
            <MyInput
              label={t("moneyInvoices_amount")}
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              error={errors.amount?.[0]}
              required={true}
            />
          </div>

          {/* التاريخ */}
          <div className="form-group">
            <MyInput
              label={t("moneyInvoices_date")}
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              error={errors.date?.[0]}
              required={true}
            />
          </div>

          {/* استلمنا نحن */}
          <div className="form-group">
            <MyInput
              label={t("moneyInvoices_receivedByUs")}
              value={formData.we}
              onChange={(e) => setFormData({ ...formData, we: e.target.value })}
              error={errors.we?.[0]}
              required={true}
            />
          </div>

          {/* المبلغ كتابةً */}
          <div className="form-group">
            <MyInput
              label={t("moneyInvoices_amountWritten")}
              value={formData.textValue}
              onChange={(e) => setFormData({ ...formData, textValue: e.target.value })}
              error={errors.textValue?.[0]}
              required={true}
            />
          </div>

          {/* لأجل */}
          <div className="form-group">
            <MyInput
              label={t("moneyInvoices_for")}
              value={formData.for}
              onChange={(e) => setFormData({ ...formData, for: e.target.value })}
              error={errors.for?.[0]}
            />
          </div>

          {/* طريقة الدفع */}
          <div className="form-group">
            <MyInput
              as="select"
              value={formData.payment_type}
              onChange={(e) => setFormData({ ...formData, payment_type: e.target.value })}
              error={errors.payment_type?.[0]}
              options={[
                { value: "", label: t("moneyInvoices_selectPaymentType") },
                { value: "cash", label: t("payment_cash") },
                { value: "bank_installment", label: t("payment_bank_installment") },
                { value: "direct_installment", label: t("payment_direct_installment") },
                { value: "network", label: t("payment_network") },
              ]}
              required={true}
            />
          </div>

          {/* ملاحظات */}
          <div className="form-group full-width">
            <MyInput
              label={t("moneyInvoices_note")}
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              error={errors.note?.[0]}
            />
          </div>
        </div>
      </ModalForm>

      {/* Modal: تأكيد الحذف */}
      <ModalForm
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={t("moneyInvoices_confirmDeleteTitle")}
        onSubmit={confirmDelete}
        loading={deleting}
        mode="confirm"
        confirmText={t("moneyInvoices_delete")}
        cancelText={t("moneyInvoices_cancel")}
      >
        {t("moneyInvoices_confirmDeleteMessage")}
      </ModalForm>

      <MyTable
        resource="money-invoices"
        body={{ type: "receive" }}
        columns={columns}
        refreshKey={refreshKey}
        title={t("receipt_voucher_title")}
        
        button={{
          text: t("receipt_voucher_add"),
          onClick: handleAdd,
          variant: "success",
          permission: "money_invoice_recive_create",
        }}
        dateFilter={{ field: "created_at", label: t("moneyInvoices_createdAt") }}
      />
    </div>
  );
}

export default ReceiptVoucher;
